/**
 * VerificationStepCard — one row in the verification hub.
 *
 * Shows the step's icon + title + description + a status badge. When the
 * step is rejected, surfaces the rejection reason in a coral-tinted notice
 * so the user can see exactly why. When the step is in active review
 * (status 'submitted'), shows an amber "under review" notice so the user
 * isn't left wondering if the upload got through.
 */
import React from 'react'
import { View, Text } from 'react-native'
import { ChevronRight, AlertCircle, Clock, Lock, type LucideIcon } from '@/components/ui/icons'
import { Pressable } from '../ui/Pressable'
import { Badge } from '../ui/Badge'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, withAlpha, FONTS, SPACING, RADIUS } from '../../lib/theme/tokens'
import { COLORS } from '../../lib/brand'
import { WEB_SHADOWS } from '../../lib/theme/web-shadow'
import type { VerificationStatus } from '../../lib/types/verification.types'

function statusVariant(
  status: VerificationStatus,
): 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info' {
  switch (status) {
    case 'approved':
      return 'success'
    case 'submitted':
      return 'warning'
    case 'rejected':
      return 'destructive'
    case 'expired':
      return 'warning'
    case 'pending':
    default:
      return 'default'
  }
}

interface VerificationStepCardProps {
  icon: LucideIcon
  title: string
  description: string
  status: VerificationStatus
  statusLabel: string
  onPress: () => void
  testID: string
  /** Rejection reason from the verification provider — shown inline under the
   *  card when status='rejected'. */
  rejectionReason?: string | null
  /** "Under review" copy — shown when status='submitted'. */
  inReviewLabel?: string
  /** When true, the card visually appears locked (lower opacity, lock icon
   *  in place of the status badge) and tapping it is a no-op. Use this for
   *  steps that have unmet upstream prerequisites — e.g. selfie can't run
   *  until identity is approved server-side. */
  disabled?: boolean
  /** Short copy explaining WHY the step is locked. Shown as the
   *  description override when `disabled` is true. */
  lockedReason?: string
}

export function VerificationStepCard({
  icon: Icon,
  title,
  description,
  status,
  statusLabel,
  onPress,
  testID,
  rejectionReason,
  inReviewLabel,
  disabled = false,
  lockedReason,
}: VerificationStepCardProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  /**
   * The reason shows whenever there IS one — the status picks the tint, not whether the person
   * is told what happened.
   *
   * Gated on `status === 'rejected'`, the retry instruction `markIdentityRetryRequired` writes
   * was unreachable by construction: it stores its sentence and leaves the row in review, whose
   * legacy status is `submitted`, so the card showed the placid "queda en revisión" and dropped
   * the one line explaining what the person actually had to do next.
   */
  const showRejection = !disabled && !!rejectionReason
  const showInReview = !disabled && status === 'submitted' && !!inReviewLabel && !rejectionReason

  return (
    <Pressable
      disabled={disabled}
      onPress={disabled ? undefined : onPress}
      accessibilityRole="button"
      accessibilityState={{ disabled }}
      accessibilityLabel={
        disabled && lockedReason ? `${title}: ${lockedReason}` : `${title}: ${statusLabel}`
      }
      testID={testID}
    >
      <View
        style={{
          padding: SPACING.md,
          borderRadius: RADIUS.lg,
          backgroundColor: colors.surface,
          borderWidth: 1,
          borderColor: status === 'rejected' ? `${COLORS.coral.DEFAULT}55` : colors.border,
          // Locked steps render at reduced opacity so the user sees the row
          // exists (visual progression / what's coming) but reads it as
          // not-yet-actionable. ~0.55 keeps text legible (WCAG-OK on the
          // surface) without making it look broken.
          opacity: disabled ? 0.72 : 1,
          ...WEB_SHADOWS.sm,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.md }}>
          <View
            style={{
              width: 48,
              height: 48,
              borderRadius: RADIUS.md,
              // Brand purple is nearly the value of the dark card, so a tint of
              // it disappeared there — visibly so on the dimmed locked row,
              // which lost its tile entirely. The light end of the ramp keeps
              // the container in both themes.
              backgroundColor: isDark
                ? withAlpha(COLORS.purple.light, 0.22)
                : withAlpha(COLORS.purple.DEFAULT, 0.07),
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon size={22} color={colors.textSecondary} strokeWidth={1.5} />
          </View>

          {/* The status pill used to sit in this row, between the text and the
              chevron, rationing the copy into a ~110pt column: every title
              broke over two lines and every description truncated. It now sits
              under the description, so the text has the row. */}
          <View style={{ flex: 1, minWidth: 0, gap: SPACING.xs }}>
            <Text
              style={{
                fontSize: 16,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
                lineHeight: 21,
              }}
              // Two lines, same as the description below it. Clamped to one,
              // "Documento de identidad" truncated to "Documento de identid…"
              // while ~130px of the row sat empty to the right of the ellipsis.
              numberOfLines={2}
            >
              {title}
            </Text>
            <Text
              style={{
                fontSize: 13,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
                lineHeight: 18,
              }}
              numberOfLines={2}
            >
              {/* When locked, the lockedReason explains WHY (e.g.
                  "Verificá primero tu identidad") — strictly more useful
                  than the generic step description. Falls back to the
                  description when no lockedReason is provided. */}
              {disabled && lockedReason ? lockedReason : description}
            </Text>
            {!disabled && (
              <View style={{ alignSelf: 'flex-start', marginTop: SPACING.xs }}>
                <Badge label={statusLabel} variant={statusVariant(status)} size="sm" />
              </View>
            )}
          </View>

          {disabled ? (
            <Lock size={16} color={colors.textMuted} strokeWidth={2} />
          ) : (
            <ChevronRight size={18} color={colors.textMuted} strokeWidth={1.5} />
          )}
        </View>

        {/* Rejection reason — coral tint, keeps the card grounded */}
        {showRejection && (
          <View
            style={{
              marginTop: SPACING.sm,
              padding: SPACING.sm,
              borderRadius: RADIUS.md,
              backgroundColor: `${COLORS.coral.DEFAULT}10`,
              flexDirection: 'row',
              gap: SPACING.sm,
              alignItems: 'flex-start',
            }}
          >
            <AlertCircle
              size={16}
              color={COLORS.coral.DEFAULT}
              strokeWidth={2}
              style={{ marginTop: 2 }}
            />
            <Text
              style={{
                flex: 1,
                fontSize: 12,
                fontFamily: FONTS.body,
                color: colors.textPrimary,
                lineHeight: 17,
              }}
            >
              {rejectionReason}
            </Text>
          </View>
        )}

        {/* In-review notice — amber, so the user knows we have their doc */}
        {showInReview && (
          <View
            style={{
              marginTop: SPACING.sm,
              padding: SPACING.sm,
              borderRadius: RADIUS.md,
              backgroundColor: `${COLORS.amber.DEFAULT}14`,
              flexDirection: 'row',
              gap: SPACING.sm,
              alignItems: 'center',
            }}
          >
            <Clock size={14} color={COLORS.amber.dark} strokeWidth={2} />
            <Text
              style={{
                flex: 1,
                fontSize: 12,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
              }}
            >
              {inReviewLabel}
            </Text>
          </View>
        )}
      </View>
    </Pressable>
  )
}
